import { RecipeSearchBar } from "@/components/recipes/RecipeSearchBar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getLabelDisplayName, getAllLabels } from "@/lib/recipe-categories";
import { getUniqueLabels, searchRecipes, type SearchFilters } from "@/lib/recipe-search-service";
import type { Recipe, RecipeBox } from "@/lib/types";
import { Search, Tag, X } from "lucide-react";
import { useMemo, useState, useEffect, useRef } from "react";

interface UnifiedSearchProps {
  recipes: Recipe[];
  recipeBox: RecipeBox | null;
  onResultsChange: (filteredRecipes: Recipe[]) => void;
  onCategorySelect?: (label: string | null) => void;
  selectedCategory?: string | null;
}

type SuggestionType = "recipe" | "label";

interface SearchSuggestion {
  type: SuggestionType;
  label: string;
  value: string;
  recipe?: Recipe;
}

/**
 * Unified search component that searches recipes and labels
 * Shows suggestions as you type and allows filtering by label
 */
export function UnifiedSearch({
  recipes,
  recipeBox,
  onResultsChange,
  onCategorySelect,
  selectedCategory,
}: UnifiedSearchProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  const boxLabels = recipeBox?.labels ?? [];
  const allLabels = getAllLabels(boxLabels);
  const allRecipeLabels = getUniqueLabels(recipes);

  const suggestions = useMemo<SearchSuggestion[]>(() => {
    if (!searchQuery.trim()) return [];

    const query = searchQuery.toLowerCase().trim();
    const results: SearchSuggestion[] = [];

    // Matching recipes (limit to 5)
    recipes
      .filter((recipe) => {
        const titleMatch = recipe.title.toLowerCase().includes(query);
        const labelMatch = recipe.labels.some((l) => l.toLowerCase().includes(query));
        const ingredientMatch = recipe.ingredients.some((ing) => ing.toLowerCase().includes(query));
        return titleMatch || labelMatch || ingredientMatch;
      })
      .slice(0, 5)
      .forEach((recipe) => {
        results.push({ type: "recipe", label: recipe.title, value: recipe.title, recipe });
      });

    // Matching labels
    [...new Set([...allLabels, ...allRecipeLabels])]
      .filter((l) => l.toLowerCase().includes(query))
      .slice(0, 5)
      .forEach((l) => {
        if (!results.some((s) => s.type === "label" && s.value === l)) {
          results.push({ type: "label", label: getLabelDisplayName(l), value: l });
        }
      });

    return results;
  }, [searchQuery, recipes, allLabels, allRecipeLabels]);

  const filteredRecipes = useMemo(() => {
    const filters: SearchFilters = {};
    if (selectedLabel) filters.labels = [selectedLabel];
    if (selectedCategory) filters.labels = [...(filters.labels ?? []), selectedCategory];

    return searchRecipes(recipes, searchQuery, filters, {
      scope: "box",
      recipeBoxId: recipeBox?.id,
    });
  }, [recipes, searchQuery, selectedLabel, selectedCategory, recipeBox?.id]);

  useEffect(() => {
    onResultsChange(filteredRecipes);
  }, [filteredRecipes, onResultsChange]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSuggestionSelect = (suggestion: SearchSuggestion) => {
    if (suggestion.type === "label") {
      setSelectedLabel(suggestion.value);
    }
    setShowSuggestions(false);
  };

  const hasActiveFilters = selectedLabel || selectedCategory;

  return (
    <div className="space-y-3" ref={searchRef}>
      <div className="relative">
        <RecipeSearchBar
          value={searchQuery}
          onChange={(value) => {
            setSearchQuery(value);
            setShowSuggestions(value.trim().length > 0);
          }}
          onFocus={() => {
            if (searchQuery.trim().length > 0) setShowSuggestions(true);
          }}
          placeholder="Search recipes or labels..."
          showHistory={false}
        />

        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute top-full z-50 mt-1 w-full rounded-md border bg-popover shadow-lg">
            <ScrollArea className="max-h-[300px]">
              <div className="p-2">
                {suggestions.map((suggestion, index) => (
                  <button
                    key={`${suggestion.type}-${suggestion.value}-${index}`}
                    type="button"
                    onClick={() => handleSuggestionSelect(suggestion)}
                    className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-sm hover:bg-accent transition-colors"
                  >
                    {suggestion.type === "recipe" && <Search className="h-4 w-4 text-muted-foreground" />}
                    {suggestion.type === "label" && <Tag className="h-4 w-4 text-muted-foreground" />}
                    <span className="flex-1 text-left">{suggestion.label}</span>
                    <span className="text-xs text-muted-foreground capitalize">{suggestion.type}</span>
                  </button>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}
      </div>

      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2">
          {selectedLabel && (
            <Badge variant="secondary" className="gap-1">
              <Tag className="h-3 w-3" />
              {selectedLabel}
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-0 ml-1 hover:bg-transparent"
                onClick={() => setSelectedLabel(null)}
                aria-label="Remove label filter"
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}
          {selectedCategory && (
            <Badge variant="secondary" className="gap-1">
              <Tag className="h-3 w-3" />
              {getLabelDisplayName(selectedCategory)}
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-0 ml-1 hover:bg-transparent"
                onClick={() => onCategorySelect?.(null)}
                aria-label="Remove label filter"
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}
        </div>
      )}

      {(searchQuery || hasActiveFilters) && (
        <p className="text-sm text-muted-foreground">
          Found {filteredRecipes.length} recipe{filteredRecipes.length !== 1 ? "s" : ""}
        </p>
      )}
    </div>
  );
}
